import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asArray, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, PackageCheck, Wrench, TerminalSquare, Monitor, Cpu,
  BatteryFull, Gauge, RefreshCw, ArrowDown, ArrowUp, FileText, Package, Rocket, AlertTriangle,
  Layers, Clock, Check, X, Loader, UserMinus, Copy, ExternalLink, ShieldAlert,
  UserRound, Plus, Save, Pencil, Wand2, Unlock, RotateCcw,
  Power, Lock, LogOut, ShieldOff, Trash2,
  Activity, Puzzle, Network, LogIn, HardDrive, KeyRound } from "lucide-react";
import { fmtTime } from "@/lib/time";
import { StatusBadge } from "@/components/Primitives";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  ["overview", "Overview", Cpu],
  ["compliance", "Compliance", ShieldCheck],
  ["patches", "Patches", PackageCheck],
  ["software", "Software", Package],
  ["system", "Processes & Services", Cpu],
  ["diagnostics", "Diagnostics", Activity],
  ["terminal", "Terminal", TerminalSquare],
  ["screen", "Remote Desktop", Monitor],
];

export default function AgentDetail() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("overview");
  const [offboard, setOffboard] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/agent/${id}`); setAgent(data); }
    catch { toast.error("Agent not found"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/agents" className="text-zinc-500 hover:text-zinc-200"><ArrowLeft size={18} /></Link>
        <h1 className="font-display text-2xl font-semibold tracking-tight">{agent?.hostname || id}</h1>
        {agent && <StatusBadge status={agent.online ? "online" : "offline"} />}
        <div className="ml-auto flex items-center gap-3">
          <ReassignClient id={id} agent={agent} onDone={load} />
          <IsolateButton id={id} agent={agent} onDone={load} />
          <button onClick={() => setOffboard(true)} data-testid="offboard-btn"
            className="inline-flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 rounded-md px-3 py-1.5 text-sm">
            <UserMinus size={14} /> Offboard
          </button>
          <span className="font-mono text-xs text-zinc-600">{id}</span>
        </div>
      </div>
      {offboard && <OffboardModal id={id} hostname={agent?.hostname || id} onClose={() => setOffboard(false)} />}

      <div className="flex gap-1 border-b border-zinc-900">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={"flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px " +
              (tab === key ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200")}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview agent={agent} id={id} />}
      {tab === "compliance" && <Compliance id={id} online={agent?.online} />}
      {tab === "patches" && <Patches id={id} online={agent?.online} />}
      {tab === "software" && <SoftwareTab id={id} online={agent?.online} />}
      {tab === "system" && <ProcSvcTab id={id} online={agent?.online} />}
      {tab === "diagnostics" && <DiagnosticsTab id={id} online={agent?.online} />}
      {tab === "terminal" && <Terminal id={id} online={agent?.online} />}
      {tab === "screen" && <Screen id={id} online={agent?.online} />}
    </div>
  );
}

function Card({ title, children, right }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
        <div className="font-display font-medium text-sm">{title}</div>{right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ k, v }) {
  return <div className="flex justify-between py-1.5 border-b border-zinc-900/60 text-sm">
    <span className="text-zinc-500">{k}</span><span className="font-mono text-zinc-200">{v ?? "—"}</span></div>;
}

function MetricBar({ label, value, unit = "%", warnGood = "high" }) {
  const v = value ?? 0;
  // warnGood 'high' → high is bad (cpu/mem/disk). 'low' → low is bad (battery).
  const bad = warnGood === "high" ? v >= 90 : v < 40;
  const warn = warnGood === "high" ? v >= 75 : v < 60;
  const color = value == null ? "#3f3f46" : bad ? "#ef4444" : warn ? "#f59e0b" : "#10b981";
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-zinc-500 uppercase">{label}</span>
        <span className="font-mono">{value == null ? "—" : value}{unit}</span>
      </div>
      <div className="h-1.5 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, v)}%`, background: color }} /></div>
    </div>
  );
}

function Overview({ agent, id }) {
  const m = agent?.metrics || {};
  const batt = agent?.last_health?.battery;
  const [speed, setSpeed] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get(`/agent/${id}/speedtests`).then(r => setSpeed(asArray(r.data)[0]?.result || null)).catch(() => {});
  }, [id]);

  const runSpeed = async () => {
    setTesting(true);
    const before = speed?.tested_at;
    try {
      await api.post(`/agent/${id}/speedtest`, {});
      for (let i = 0; i < 24; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const { data } = await api.get(`/agent/${id}/speedtests`);
        const latest = asArray(data)[0]?.result;
        if (latest && latest.tested_at !== before) { setSpeed(latest); break; }
      }
    } catch { toast.error("Could not run speed test"); }
    finally { setTesting(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Card title="System">
        <Field k="Hostname" v={agent?.hostname} />
        <Field k="OS" v={`${agent?.os || ""} ${agent?.os_version || ""}`} />
        <Field k="Serial" v={agent?.serial_number} />
        <Field k="Vendor" v={[agent?.manufacturer, agent?.model].filter(Boolean).join(" ")} />
        <Field k="Agent version" v={agent?.version} />
        <Field k="Last heartbeat" v={agent?.last_heartbeat ? fmtTime(agent.last_heartbeat) : "—"} />
      </Card>

      <Card title="Live metrics">
        <MetricBar label="cpu" value={m.cpu} />
        <MetricBar label="mem" value={m.mem} />
        <MetricBar label="disk" value={m.disk} />
      </Card>

      <Card title="Battery lifecycle health" right={<BatteryFull size={15} className="text-zinc-500" />}>
        {!batt || batt.present === false ? (
          <div className="text-sm text-zinc-600">No battery detected (desktop or VM).</div>
        ) : (
          <>
            <MetricBar label="lifecycle health" value={batt.health_percent || null} warnGood="low" />
            <MetricBar label="charge" value={batt.percent} warnGood="low" />
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Stat label="Cycles" value={batt.cycle_count ?? "—"} />
              <Stat label="Full charge" value={batt.full_capacity_mwh ? `${(batt.full_capacity_mwh / 1000).toFixed(1)} Wh` : "—"} />
              <Stat label="Design" value={batt.design_capacity_mwh ? `${(batt.design_capacity_mwh / 1000).toFixed(1)} Wh` : "—"} />
            </div>
            <div className="text-[11px] text-zinc-600 mt-3">{batt.charging ? "Charging" : "On battery"}
              {batt.health_percent ? ` · ${batt.health_percent}% of original capacity remaining` : " · lifecycle health needs an elevated agent"}</div>
          </>
        )}
      </Card>

      <Card title="Internet speed"
        right={<button onClick={runSpeed} disabled={testing || !agent?.online}
          className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-1.5 text-zinc-300 disabled:opacity-40">
          <RefreshCw size={12} className={testing ? "animate-spin" : ""} /> {testing ? "Testing…" : "Run test"}</button>}>
        {!speed ? (
          <div className="text-sm text-zinc-600">{testing ? "Running speed test (10–20s)…" : "No test yet — run one."}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-zinc-900/40 border border-zinc-900 rounded p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500"><ArrowDown size={12} className="text-emerald-400" /> Download</div>
                <div className="font-mono text-2xl text-zinc-100 mt-1">{speed.download_mbps}<span className="text-sm text-zinc-500 ml-1">Mbps</span></div>
              </div>
              <div className="bg-zinc-900/40 border border-zinc-900 rounded p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500"><ArrowUp size={12} className="text-blue-400" /> Upload</div>
                <div className="font-mono text-2xl text-zinc-100 mt-1">{speed.upload_mbps}<span className="text-sm text-zinc-500 ml-1">Mbps</span></div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Latency" value={`${speed.latency_ms} ms`} />
              <Stat label="Jitter" value={`${speed.jitter_ms} ms`} />
              <Stat label="DNS" value={`${speed.dns_ms} ms`} />
            </div>
            <div className="text-[11px] text-zinc-600 mt-3">Tested {fmtTime(speed.tested_at)} · via {(speed.server || "").replace(/^https?:\/\//, "")}</div>
          </>
        )}
      </Card>

      <div className="md:col-span-2"><DiskEncryptionCard agent={agent} id={id} online={agent?.online} /></div>
      <div className="md:col-span-2"><OwnerAssetsCard agent={agent} id={id} /></div>
      <div className="md:col-span-2"><RemediationTasksCard id={id} online={agent?.online} /></div>
      <div className="md:col-span-2"><PowerCard id={id} online={agent?.online} /></div>
    </div>
  );
}

function IsolateButton({ id, agent, onDone }) {
  const isolated = !!agent?.isolated;
  const toggle = async () => {
    const next = !isolated;
    if (!window.confirm(next
      ? "Network-isolate this device? It will lose all network access EXCEPT the AegisOne management channel, so you can still reach it here to lift isolation."
      : "Lift network isolation and restore normal connectivity?")) return;
    try {
      const { data } = await api.post(`/agent/${id}/isolate`, { isolate: next });
      toast.success(next ? "Isolation queued" : "Lifting isolation");
      onDone && onDone();
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <button onClick={toggle} data-testid="isolate-btn" title="Network-quarantine this device"
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm border ${isolated
        ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
        : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-500/40"}`}>
      <ShieldOff size={14} /> {isolated ? "Isolated" : "Isolate"}
    </button>
  );
}

const POWER = [
  { key: "reboot", label: "Reboot", icon: RotateCcw, danger: true, confirm: "Reboot this device now?" },
  { key: "shutdown", label: "Shut down", icon: Power, danger: true, confirm: "Shut down this device now? It will go offline until powered back on." },
  { key: "lock", label: "Lock screen", icon: Lock, danger: false, confirm: null },
  { key: "signout", label: "Sign out user", icon: LogOut, danger: true, confirm: "Sign out the signed-in user? Unsaved work may be lost." },
];

function PowerCard({ id, online }) {
  const [busy, setBusy] = useState("");
  const run = async (p) => {
    if (p.confirm && !window.confirm(p.confirm)) return;
    setBusy(p.key);
    try {
      const { data } = await api.post(`/agent/${id}/power`, { action: p.key });
      toast.success(`${p.label} → ${data.delivered ? "sent" : "queued (offline)"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <Card title="Power" right={<Power size={15} className="text-zinc-500" />}>
      {!online && <div className="text-[11px] text-amber-500/80 mb-3">Device is offline — the action runs on its next check-in.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {POWER.map(p => {
          const Icon = p.icon;
          return (
            <button key={p.key} onClick={() => run(p)} disabled={busy === p.key}
              className={`flex flex-col items-center gap-1.5 rounded p-3 border transition-colors disabled:opacity-50 ${p.danger ? "bg-zinc-900/40 border-zinc-900 hover:border-red-500/40" : "bg-zinc-900/40 border-zinc-900 hover:border-blue-500/40"}`}>
              <Icon size={18} className={p.danger ? "text-red-300/80" : "text-zinc-300"} />
              <span className="text-xs text-zinc-200">{busy === p.key ? "…" : p.label}</span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

const TASK_ICON = {
  unlock_accounts: Unlock, restart_explorer: RotateCcw, restart_outlook: RotateCcw, restart_teams: RotateCcw,
  defender_scan: ShieldCheck, flush_dns: RefreshCw, gpupdate: RefreshCw, disk_cleanup: Trash2,
};
const DISRUPTIVE = new Set(["restart_explorer", "restart_outlook", "restart_teams"]);

function RemediationTasksCard({ id, online }) {
  const [tasks, setTasks] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");

  const loadLog = () => api.get(`/agent/${id}/action-log`).then(r =>
    setLog(asArray(r.data).filter(x => x.type === "action_result" || x.status).slice(0, 6))).catch(() => {});
  useEffect(() => {
    api.get("/remediation/tasks").then(r => setTasks(asArray(r.data))).catch(() => setTasks([]));
    loadLog(); const t = setInterval(loadLog, 6000); return () => clearInterval(t);
  }, [id]);

  const run = async (task) => {
    if (DISRUPTIVE.has(task.id) && !window.confirm(`${task.name} on this device? This will close the app for the signed-in user and reopen it.`)) return;
    setBusy(task.id);
    try {
      const { data } = await api.post(`/agent/${id}/remediation/task`, { task_id: task.id });
      toast.success(`${task.name} → ${data.delivered ? "sent" : "queued (offline)"}`);
      setTimeout(loadLog, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <Card title="Remediation tasks" right={<Wand2 size={15} className="text-zinc-500" />}>
      {!online && <div className="text-[11px] text-amber-500/80 mb-3">Device is offline — a task will run on its next check-in.</div>}
      <div className="grid sm:grid-cols-2 gap-2">
        {tasks.map(t => {
          const Icon = TASK_ICON[t.id] || Wand2;
          return (
            <button key={t.id} onClick={() => run(t)} disabled={busy === t.id}
              className={`flex items-start gap-3 text-left rounded p-3 border transition-colors disabled:opacity-50 ${DISRUPTIVE.has(t.id) ? "bg-zinc-900/40 border-zinc-900 hover:border-amber-500/40" : "bg-zinc-900/40 border-zinc-900 hover:border-blue-500/40"}`}>
              <Icon size={16} className="text-zinc-400 shrink-0 mt-0.5" />
              <span className="min-w-0">
                <span className="block text-sm text-zinc-100">{busy === t.id ? "Running…" : t.name}</span>
                <span className="block text-[11px] text-zinc-500">{t.desc}</span>
              </span>
            </button>
          );
        })}
      </div>
      {log.length > 0 && (
        <div className="mt-4 pt-3 border-t border-zinc-900">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Recent results</div>
          <div className="space-y-1.5">
            {log.map((x, i) => {
              const out = (x.output || x.result?.output || "").trim();
              const ok = (x.status || x.result?.status) === "ok";
              return (
                <div key={i} className="text-[11px] flex items-start gap-2">
                  <span className={ok ? "text-emerald-400" : "text-amber-400"}>{ok ? "✓" : "•"}</span>
                  <span className="text-zinc-400 font-mono truncate">{out || (x.status || "…")}</span>
                  <span className="text-zinc-600 ml-auto shrink-0">{fmtTime(x.at)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function ReassignClient({ id, agent, onDone }) {
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/clients").then(r => setClients(asArray(r.data))).catch(() => setClients([])); }, []);
  const change = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/agents/${id}/assign`, { client_id: cid });
      toast.success(cid ? "Reassigned to client" : "Unassigned");
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <label className="flex items-center gap-1.5 text-xs text-zinc-500" title="Reassign this device to another client">
      <UserRound size={13} className="text-zinc-500" />
      <select value={agent?.client_id || ""} disabled={busy} onChange={e => change(e.target.value)}
        data-testid="reassign-client"
        className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-200 outline-none focus:border-zinc-600 disabled:opacity-50">
        <option value="">Unassigned</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </label>
  );
}

function OwnerAssetsCard({ agent, id }) {
  const [owner, setOwner] = useState("");
  const [editing, setEditing] = useState(false);
  const [assets, setAssets] = useState([]);
  const [adding, setAdding] = useState(null); // draft peripheral or null
  const TYPES = ["Monitor", "Headset", "Keyboard", "Mouse", "Docking station", "Webcam", "Phone", "Tablet", "Printer", "External drive", "Other"];

  const load = () => api.get(`/peripherals?agent_id=${id}`).then(r => setAssets(asArray(r.data))).catch(() => {});
  useEffect(() => { load(); }, [id]);
  useEffect(() => { setOwner(agent?.assigned_user || ""); }, [agent?.assigned_user]);

  const saveOwner = async () => {
    try { await api.put(`/agent/${id}/assigned-user`, { assigned_user: owner }); setEditing(false); toast.success("Owner updated"); }
    catch { toast.error("Failed"); }
  };
  const saveAsset = async () => {
    if (!adding.type && !adding.make_model) { toast.error("Type or make/model required"); return; }
    try {
      const body = { ...adding, agent_id: id, assigned_user: adding.assigned_user || owner };
      if (adding.id) await api.put(`/peripherals/${adding.id}`, body); else await api.post("/peripherals", body);
      setAdding(null); load(); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.type}?`)) return; try { await api.delete(`/peripherals/${p.id}`); load(); } catch { toast.error("Failed"); } };
  const inp = "bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 outline-none w-full";

  return (
    <Card title="Owner & assigned assets" right={<UserRound size={15} className="text-zinc-500" />}>
      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-zinc-900">
        <span className="text-sm text-zinc-500">Owned by</span>
        {editing ? (
          <>
            <input autoFocus value={owner} onChange={e => setOwner(e.target.value)} onKeyDown={e => e.key === "Enter" && saveOwner()}
              className={inp + " max-w-xs"} placeholder="name / email" />
            <button onClick={saveOwner} className="text-emerald-400 p-1"><Check size={15} /></button>
            <button onClick={() => { setEditing(false); setOwner(agent?.assigned_user || ""); }} className="text-zinc-500 p-1"><X size={15} /></button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 group">
            {owner ? <span className="text-zinc-100 font-medium">{owner}</span> : <span className="text-zinc-600 italic">Unassigned</span>}
            <Pencil size={12} className="text-zinc-700 group-hover:text-zinc-400" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Assigned hardware ({assets.length})</div>
        <button onClick={() => setAdding({ type: "Monitor", make_model: "", serial_number: "", assigned_user: owner, notes: "" })}
          className="inline-flex items-center gap-1 text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-1.5 text-zinc-300"><Plus size={12} /> Add asset</button>
      </div>
      {assets.length === 0 && !adding && <div className="text-sm text-zinc-600">No peripherals registered to this device.</div>}
      <div className="space-y-1.5">
        {assets.map(p => (
          <div key={p.id} className="flex items-center justify-between gap-2 bg-zinc-900/40 border border-zinc-900 rounded px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="text-zinc-100">{p.type}</span>
              {p.make_model && <span className="text-zinc-500"> · {p.make_model}</span>}
              {p.serial_number && <span className="text-zinc-600 font-mono text-xs"> · {p.serial_number}</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] uppercase font-mono ${p.status === "assigned" ? "text-emerald-400" : "text-zinc-500"}`}>{p.status}</span>
              <button onClick={() => setAdding({ ...p })} className="p-1 text-zinc-500 hover:text-zinc-200"><Pencil size={12} /></button>
              <button onClick={() => del(p)} className="p-1 text-red-400/70 hover:text-red-300"><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {adding && (
        <div className="mt-3 bg-zinc-900/40 border border-zinc-800 rounded p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <select className={inp} value={adding.type} onChange={e => setAdding(a => ({ ...a, type: e.target.value }))}>{TYPES.map(t => <option key={t}>{t}</option>)}</select>
            <input className={inp} placeholder="Serial number" value={adding.serial_number} onChange={e => setAdding(a => ({ ...a, serial_number: e.target.value }))} />
          </div>
          <input className={inp} placeholder="Make / model (e.g. Dell U2723QE)" value={adding.make_model} onChange={e => setAdding(a => ({ ...a, make_model: e.target.value }))} />
          <input className={inp} placeholder="Owned by (defaults to device owner)" value={adding.assigned_user} onChange={e => setAdding(a => ({ ...a, assigned_user: e.target.value }))} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(null)} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
            <button onClick={saveAsset} className="inline-flex items-center gap-1.5 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded px-3 py-1.5 text-sm"><Save size={13} /> Save</button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-900 rounded p-2 text-center">
      <div className="font-mono text-base text-zinc-100">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function sevColor(s) {
  return { critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-emerald-400" }[s] || "text-zinc-400";
}

// Each hardening control is mapped to the framework(s) it evidences and, for
// ISO/IEC 27001, its Annex A clause. This is the multi-framework mapping: one
// scan, reported against whichever framework the operator selects.
const CONTROL_MAP = {
  "CIS-18.9.11": { iso: "A.8.24", e8: true }, // BitLocker
  "CIS-2.6.1.1": { iso: "A.8.24" },           // FileVault
  "NIST-SC-TPM": { iso: "A.8.24" },           // TPM
  "CIS-DEF-1": { iso: "A.8.7", e8: true }, "CIS-DEF-2": { iso: "A.8.7", e8: true }, "CIS-DEF-3": { iso: "A.8.7", e8: true },
  "CIS-2.6.2": { iso: "A.8.7" },              // Gatekeeper
  "CIS-9.1": { iso: "A.8.20" }, "CIS-2.5.2.1": { iso: "A.8.20" }, // firewalls
  "CIS-18.3.1": { iso: "A.8.20" }, "CIS-2.4.1": { iso: "A.8.20" }, // SMBv1 / SSH
  "NIST-SC-SB": { iso: "A.8.8" },             // Secure Boot
  "CIS-18.9.WU": { iso: "A.8.8", e8: true }, "CIS-1.1": { iso: "A.8.8", e8: true }, "CIS-1.4": { iso: "A.8.8", e8: true },
  "CIS-1.6": { iso: "A.8.9" },                // SIP
  "CIS-2.3.17": { iso: "A.8.2", e8: true }, "CIS-2.3.1.2": { iso: "A.8.2" }, "CIS-2.11": { iso: "A.8.2" }, // privilege / guest
  "CIS-18.9.RDP": { iso: "A.8.5" }, "CIS-1.1.4": { iso: "A.8.5" }, "CIS-2.3.1": { iso: "A.8.5" }, // auth
};
const FRAMEWORKS = [
  { key: "all", label: "All frameworks" },
  { key: "iso", label: "ISO/IEC 27001" },
  { key: "cis", label: "CIS Benchmarks" },
  { key: "nist", label: "NIST CSF" },
  { key: "e8", label: "Essential Eight" },
];
function inFramework(controlId, fw) {
  if (fw === "all") return true;
  if (fw === "cis") return controlId.startsWith("CIS");
  if (fw === "nist") return controlId.startsWith("NIST");
  const m = CONTROL_MAP[controlId] || {};
  if (fw === "iso") return !!m.iso;
  if (fw === "e8") return !!m.e8;
  return true;
}

function Compliance({ id, online }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fw, setFw] = useState("all");
  const [exFor, setExFor] = useState(null); // control being documented
  const [exForm, setExForm] = useState({ kind: "compensating_control", justification: "", compensating_control: "", expires_at: "" });
  const load = async () => { const { data } = await api.get(`/agent/${id}/compliance`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { setBusy(true); try { await api.post(`/agent/${id}/compliance/scan`); toast.success("Compliance scan requested"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const remediate = async (controlId) => { try { const r = await api.post(`/agent/${id}/remediate?control_id=${encodeURIComponent(controlId)}`); toast.success(`Remediation ${r.data.delivered ? "dispatched" : "queued"}`); } catch (e) { toast.error(e.response?.data?.detail || "No remediation available"); } };

  const openException = (c) => {
    const ex = c.exception;
    setExForm(ex ? { kind: ex.kind, justification: ex.justification || "", compensating_control: ex.compensating_control || "", expires_at: ex.expires_at || "" }
                 : { kind: "compensating_control", justification: "", compensating_control: "", expires_at: "" });
    setExFor(c);
  };
  const saveException = async () => {
    if (!exForm.justification.trim()) { toast.error("Justification is required"); return; }
    try {
      await api.post(`/agent/${id}/compliance/exception`, {
        control_id: exFor.control_id, iso_clause: CONTROL_MAP[exFor.control_id]?.iso || "",
        ...exForm,
      });
      toast.success("Exception documented — control now waived");
      setExFor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeException = async (controlId) => {
    try { await api.delete(`/agent/${id}/compliance/exception/${encodeURIComponent(controlId)}`); toast.success("Exception removed"); await load(); }
    catch { toast.error("Failed"); }
  };

  const all = data?.controls || [];
  const controls = all.filter(c => inFramework(c.control_id, fw));
  const evaluated = controls.filter(c => c.status === "pass" || c.status === "fail");
  const assessed = controls.filter(c => c.status === "pass" || c.status === "fail" || c.waived);
  const score = evaluated.length ? Math.round(controls.filter(c => c.status === "pass").length * 100 / evaluated.length) : null;
  const scoreEx = assessed.length ? Math.round(controls.filter(c => c.status === "pass" || c.waived).length * 100 / assessed.length) : null;
  const openGaps = controls.filter(c => c.status === "fail" && !c.waived).length;
  const fwLabel = FRAMEWORKS.find(f => f.key === fw)?.label;

  return (
    <Card title={`Compliance audit ${score != null ? `· ${score}%` : ""}`}
      right={
        <div className="flex items-center gap-2">
          <select value={fw} onChange={e => setFw(e.target.value)} data-testid="framework-select"
            className="bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-200 focus:border-zinc-600 outline-none">
            {FRAMEWORKS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <button onClick={() => window.open(`${API}/agent/${id}/compliance/gap-report.pdf`, "_blank")}
            className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 text-xs rounded px-3 py-1.5"><FileText size={12} /> Gap report</button>
          <button onClick={scan} disabled={busy || !online} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Run scan</button>
        </div>
      }>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500 mb-3 -mt-1">
        {fw !== "all" && <span>Showing <span className="text-zinc-300">{controls.length}</span> mapped to <span className="text-zinc-300">{fwLabel}</span></span>}
        <span>Raw: <span className={score >= 85 ? "text-emerald-400" : score >= 60 ? "text-amber-400" : "text-red-400"}>{score ?? "—"}%</span></span>
        <span>With documented exceptions: <span className={scoreEx >= 85 ? "text-emerald-400" : scoreEx >= 60 ? "text-amber-400" : "text-red-400"}>{scoreEx ?? "—"}%</span></span>
        <span>{openGaps === 0 ? <span className="text-emerald-400">No open gaps — fully compliant</span> : <span className="text-red-400">{openGaps} open gap{openGaps === 1 ? "" : "s"}</span>}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-2">Control</th>{fw === "iso" && <th className="text-left">ISO clause</th>}<th className="text-left">Title</th><th className="text-left">Severity</th><th className="text-left">Status</th><th></th></tr></thead>
        <tbody>
          {controls.map((c) => (
            <tr key={c.control_id} className="border-t border-zinc-900/60 align-top">
              <td className="py-2 font-mono text-xs">{c.control_id}</td>
              {fw === "iso" && <td className="font-mono text-xs text-cyan-400 py-2">{CONTROL_MAP[c.control_id]?.iso || "—"}</td>}
              <td className="text-zinc-300 py-2">
                {c.title}
                {c.waived && c.exception && (
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    <span className="text-amber-400/80">{c.exception.kind === "risk_accepted" ? "Risk accepted" : "Compensating control"}:</span> {c.exception.compensating_control || c.exception.justification}
                  </div>
                )}
              </td>
              <td className={"text-xs py-2 " + sevColor(c.severity)}>{c.severity}</td>
              <td className="text-xs font-medium py-2">
                {c.waived ? <span className="text-amber-400">waived</span>
                  : <span className={c.status === "pass" ? "text-emerald-400" : c.status === "fail" ? "text-red-400" : "text-zinc-500"}>{c.status}</span>}
              </td>
              <td className="text-right py-2 whitespace-nowrap">
                {(c.status === "fail" || c.status === "unknown") && (
                  <div className="flex items-center gap-3 justify-end">
                    {c.status === "fail" && !c.waived && <button onClick={() => remediate(c.control_id)} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"><Wrench size={12} />Remediate</button>}
                    {c.waived
                      ? <button onClick={() => removeException(c.control_id)} className="text-xs text-zinc-500 hover:text-zinc-300">Remove waiver</button>
                      : <button onClick={() => openException(c)} className="flex items-center gap-1 text-xs text-amber-400/90 hover:text-amber-300"><FileText size={12} />Document exception</button>}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {controls.length === 0 && <tr><td colSpan={fw === "iso" ? 6 : 5} className="py-6 text-zinc-500">{all.length === 0 ? "No compliance data — run a scan." : `No controls mapped to ${fwLabel} on this device.`}</td></tr>}
        </tbody>
      </table>

      {exFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setExFor(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-md">
            <div className="px-5 py-3.5 border-b border-zinc-900 text-sm font-medium text-zinc-100">Document ISO exception · {exFor.control_id}</div>
            <div className="p-5">
              <div className="text-xs text-zinc-500 mb-3">{exFor.title}{CONTROL_MAP[exFor.control_id]?.iso ? ` · ISO ${CONTROL_MAP[exFor.control_id].iso}` : ""}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Type</div>
              <div className="flex gap-2 mb-3">
                {[["compensating_control", "Compensating control"], ["risk_accepted", "Risk accepted"]].map(([k, l]) => (
                  <button key={k} onClick={() => setExForm(f => ({ ...f, kind: k }))} className={`flex-1 rounded px-3 py-2 text-xs border ${exForm.kind === k ? "bg-amber-500/15 border-amber-500/40 text-amber-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>{l}</button>
                ))}
              </div>
              {exForm.kind === "compensating_control" && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Compensating control in place</div>
                  <textarea className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 mb-3" rows={2}
                    placeholder="e.g. Device kept on premises in a locked facility; full-disk encryption enforced at the file-share layer"
                    value={exForm.compensating_control} onChange={e => setExForm(f => ({ ...f, compensating_control: e.target.value }))} />
                </>
              )}
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Justification (required)</div>
              <textarea className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 mb-3" rows={3}
                placeholder="Why the technical control cannot be applied, and why the residual risk is acceptable."
                value={exForm.justification} onChange={e => setExForm(f => ({ ...f, justification: e.target.value }))} />
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Review by (optional)</div>
              <input type="date" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 mb-4"
                value={exForm.expires_at} onChange={e => setExForm(f => ({ ...f, expires_at: e.target.value }))} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setExFor(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
                <button onClick={saveException} className="bg-amber-500/15 border border-amber-500/40 text-amber-200 rounded-md px-4 py-2 text-sm">Save exception</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function InstallStatus({ status }) {
  const s = (status || "queued").toLowerCase();
  if (s === "installed" || s === "completed")
    return <span className="inline-flex items-center gap-1 text-emerald-400"><Check size={12} /> installed</span>;
  if (s === "failed" || s === "error")
    return <span className="inline-flex items-center gap-1 text-red-400"><X size={12} /> failed</span>;
  return <span className="inline-flex items-center gap-1 text-amber-400"><Loader size={12} className="animate-spin" /> {s}</span>;
}

function SoftwareTab({ id, online }) {
  const [pkgs, setPkgs] = useState(null);
  const [history, setHistory] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [flagged, setFlagged] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);   // expanded history row id (shows install output)
  const [q, setQ] = useState("");

  const loadHistory = () => api.get(`/agent/${id}/install-history`).then(r => setHistory(asArray(r.data))).catch(() => {});
  useEffect(() => {
    api.get("/packages").then(r => setPkgs(asArray(r.data))).catch(() => setPkgs([]));
    api.get(`/agent/${id}/bundles`).then(r => setBundles(asArray(r.data))).catch(() => setBundles([]));
    api.get(`/agent/${id}/unauthorised`).then(r => setFlagged(r.data)).catch(() => setFlagged({ count: 0, flagged: [] }));
    loadHistory(); const t = setInterval(loadHistory, 6000); return () => clearInterval(t);
  }, [id]);

  const install = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/agent/${id}/deploy-package`, { package_id: p.id });
      toast.success(`${p.name} → install ${data.delivered ? "sent" : "queued"}`);
      setTimeout(loadHistory, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const deployBundle = async (b) => {
    setBusy(b.id);
    try {
      const { data } = await api.post(`/bundles/${b.id}/deploy`, { agent_id: id });
      toast.success(`${b.name} → ${data.queued} package${data.queued === 1 ? "" : "s"} queued`);
      setTimeout(loadHistory, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const shown = (pkgs || []).filter(p => !q || `${p.name} ${p.publisher}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        {bundles.length > 0 && (
          <Card title="Deployment bundles" right={<Layers size={14} className="text-zinc-500" />}>
            <div className="grid sm:grid-cols-2 gap-2">
              {bundles.map(b => (
                <div key={b.id} className="flex items-center justify-between gap-2 bg-zinc-900/40 border border-zinc-900 rounded px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{b.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{(b.packages || []).length} app{(b.packages || []).length === 1 ? "" : "s"}: {(b.packages || []).map(p => p.name).join(", ") || "empty"}</div>
                  </div>
                  <button onClick={() => deployBundle(b)} disabled={busy === b.id || !(b.packages || []).length}
                    className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded px-2.5 py-1.5 text-xs shrink-0 disabled:opacity-50">
                    <Rocket size={12} /> {busy === b.id ? "…" : "Deploy"}
                  </button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-zinc-600 mt-3">Bundles are defined per client on the Clients page. Deploying installs every app in the bundle silently on this device.</div>
          </Card>
        )}

        <Card title="Install software"
          right={<input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…" className="bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1 text-xs text-zinc-200 outline-none" />}>
          {!online && <div className="text-[11px] text-amber-500/80 mb-3">Device is offline — an install will run on its next check-in.</div>}
          {pkgs === null ? <div className="text-zinc-500 text-sm">Loading catalogue…</div> :
            <div className="grid sm:grid-cols-2 gap-2">
              {shown.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-2 bg-zinc-900/40 border border-zinc-900 rounded px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{p.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{p.publisher || "—"} · {p.method}</div>
                  </div>
                  <button onClick={() => install(p)} disabled={busy === p.id}
                    className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1.5 text-xs shrink-0 disabled:opacity-50">
                    <Rocket size={12} /> {busy === p.id ? "…" : "Install"}
                  </button>
                </div>
              ))}
            </div>}
          <div className="text-[11px] text-zinc-600 mt-3">Silent install via the package catalogue. Manage the catalogue on the Software page. Reliable machine-wide installs need the agent running as the SYSTEM service (or a client deployment credential).</div>
        </Card>

        {/* Installation history — every deploy queued to this device, newest first */}
        <Card title="Installation history" right={<Clock size={14} className="text-zinc-500" />}>
          {history.length === 0 ? <div className="text-sm text-zinc-600">No installs yet on this device.</div> :
            <div className="divide-y divide-zinc-900/60">
              {history.map((h) => {
                const expandable = h.output || h.error;
                return (
                  <div key={h.id} className="py-2 text-xs">
                    <div className={`flex items-center justify-between gap-3 ${expandable ? "cursor-pointer" : ""}`}
                      onClick={() => expandable && setOpen(open === h.id ? null : h.id)}>
                      <div className="min-w-0">
                        <div className="text-zinc-100 truncate">{h.name || "package"}</div>
                        <div className="text-[10px] text-zinc-600">
                          {fmtTime(h.requested_at)}
                          {h.requested_by ? ` · ${h.requested_by}` : ""}
                          {!h.delivered && h.status === "queued" ? " · awaiting check-in" : ""}
                        </div>
                      </div>
                      <div className="shrink-0 font-mono text-[11px]"><InstallStatus status={h.status} /></div>
                    </div>
                    {h.error && open !== h.id && <div className="text-[10px] text-red-400/80 truncate mt-0.5">{h.error}</div>}
                    {open === h.id && (
                      <pre className="mt-1.5 bg-black/50 border border-zinc-900 rounded p-2 text-[10px] text-zinc-400 whitespace-pre-wrap max-h-52 overflow-auto">{h.error ? `ERROR: ${h.error}\n` : ""}{h.output || "(no output captured)"}</pre>
                    )}
                  </div>
                );
              })}
            </div>}
        </Card>
      </div>

      <Card title={`Unauthorised software${flagged?.count ? ` · ${flagged.count}` : ""}`}
        right={<AlertTriangle size={14} className={flagged?.count ? "text-amber-400" : "text-zinc-600"} />}>
        {flagged === null ? <div className="text-sm text-zinc-600">Scanning…</div> :
          flagged.count === 0 ? <div className="flex items-center gap-2 text-emerald-400/90 text-sm"><ShieldCheck size={14} /> No flagged software.</div> :
          <div className="space-y-2">
            {flagged.flagged.map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs border-b border-zinc-900/60 pb-2">
                <div className="min-w-0">
                  <div className="text-zinc-100 truncate">{f.name}</div>
                  <div className="text-[10px] text-zinc-600">{f.version}</div>
                </div>
                <span className="text-[10px] uppercase font-mono text-amber-400 whitespace-nowrap">{f.category}</span>
              </div>
            ))}
            <div className="text-[10px] text-zinc-600 pt-1">Remote-access, P2P, mining or blocklisted apps found in inventory.</div>
          </div>}
      </Card>
    </div>
  );
}

function OffboardModal({ id, hostname, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [steps, setSteps] = useState({ change_password: true, disable_account: false, copy_files: true, wipe: false });
  const [wipeMode, setWipeMode] = useState("profile");
  const [confirm, setConfirm] = useState("");
  const [driveOn, setDriveOn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {new_password, job_id}
  const [jobs, setJobs] = useState([]);

  const loadJobs = () => api.get(`/agent/${id}/offboard-jobs`).then(r => setJobs(asArray(r.data))).catch(() => {});
  useEffect(() => {
    api.get(`/agent/${id}/accounts`).then(r => {
      // exclude admins and Windows built-in / system accounts — never offboarding targets
      const SYS = new Set(["defaultaccount", "wdagutilityaccount", "guest", "defaultuser0", "administrator"]);
      const list = asArray(r.data?.accounts).filter(a => !a.is_admin && !SYS.has((a.name || "").toLowerCase()));
      setAccounts(list); if (list[0]) setAccount(list[0].name);
    }).catch(() => {});
    api.get("/offboard/config").then(r => setDriveOn(!!r.data?.gdrive_configured)).catch(() => setDriveOn(false));
    loadJobs(); const t = setInterval(loadJobs, 5000); return () => clearInterval(t);
  }, [id]);

  const wipeConfirmed = !steps.wipe || confirm.trim().toLowerCase() === hostname.toLowerCase();
  const anyStep = steps.change_password || steps.disable_account || steps.copy_files || steps.wipe;

  const run = async () => {
    if (!account) { toast.error("Pick the departing user's account"); return; }
    if (!anyStep) { toast.error("Select at least one step"); return; }
    if (steps.wipe && !wipeConfirmed) { toast.error(`Type the hostname (${hostname}) to confirm the wipe`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/agent/${id}/offboard`, {
        account, change_password: steps.change_password, disable_account: steps.disable_account,
        copy_files: steps.copy_files, wipe: steps.wipe, wipe_mode: wipeMode,
      });
      setResult(data);
      toast.success("Offboarding queued");
      setTimeout(loadJobs, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const box = "bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 outline-none w-full";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900 sticky top-0 bg-zinc-950">
          <div className="flex items-center gap-2 text-sm font-medium text-zinc-100"><UserMinus size={15} className="text-red-400" /> Offboard user · {hostname}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {!result ? (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Departing user (admins excluded)</div>
                {accounts.length === 0
                  ? <div className="text-xs text-amber-500/80">No non-admin local accounts found — run an Accounts scan on the device first.</div>
                  : <select className={box} value={account} onChange={e => setAccount(e.target.value)}>
                      {accounts.map(a => <option key={a.name} value={a.name}>{a.name}{a.full_name ? ` (${a.full_name})` : ""}{a.enabled === false ? " · disabled" : ""}</option>)}
                    </select>}
              </div>

              <div className="space-y-2">
                <StepRow on={steps.change_password} onToggle={v => setSteps(s => ({ ...s, change_password: v }))}
                  title="Change the user's password" hint="Locks them out immediately. A strong password is generated and shown to you once." />
                <StepRow on={steps.disable_account} onToggle={v => setSteps(s => ({ ...s, disable_account: v }))}
                  title="Disable the account" hint="Blocks sign-in without deleting the profile — reversible. Softer than removing the profile." />
                <StepRow on={steps.copy_files} onToggle={v => setSteps(s => ({ ...s, copy_files: v }))}
                  title="Copy files to Google Drive" hint={driveOn === false
                    ? "Google Drive is not configured — files are archived and served from a private AegisOne link (editor-equivalent) instead."
                    : "Archives the user's data folders and uploads to Google Drive, shared to anyone-with-the-link as editor."} />
                <StepRow on={steps.wipe} danger onToggle={v => setSteps(s => ({ ...s, wipe: v, }))}
                  title="Remove profile / wipe device" hint="Destructive. Runs LAST, and is skipped automatically if a requested copy fails." />
              </div>

              {steps.wipe && (
                <div className="bg-red-500/5 border border-red-500/25 rounded p-3 space-y-2.5">
                  <div className="flex items-center gap-2 text-red-300 text-xs"><ShieldAlert size={14} /> Destructive action — confirm carefully</div>
                  <div className="flex gap-2">
                    {[["profile", "Delete user profile"], ["device", "Full device wipe"]].map(([v, l]) => (
                      <button key={v} onClick={() => setWipeMode(v)}
                        className={`flex-1 rounded px-2.5 py-1.5 text-xs border ${wipeMode === v ? "bg-red-500/15 border-red-500/50 text-red-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>{l}</button>
                    ))}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {wipeMode === "device"
                      ? "Initiates a Windows factory reset — erases ALL data and terminates the agent. Use only for a returned/retired device."
                      : "Removes only the selected user's profile and account; other users and the device are untouched."}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Type the hostname to confirm: <span className="text-zinc-300">{hostname}</span></div>
                    <input className={box} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={hostname} />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
                <button onClick={run} disabled={busy || !anyStep || !wipeConfirmed || !account}
                  className="inline-flex items-center gap-2 bg-red-500/15 border border-red-500/40 text-red-200 rounded-md px-4 py-2 text-sm disabled:opacity-40">
                  <UserMinus size={15} /> {busy ? "Queuing…" : "Run offboarding"}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><Check size={15} /> Offboarding queued for <span className="font-mono">{account}</span>.</div>
              {result.new_password && (
                <div className="bg-zinc-900/50 border border-zinc-800 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">New password (shown once — save it)</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm text-amber-300 font-mono break-all">{result.new_password}</code>
                    <button onClick={() => copy(result.new_password)} className="p-1.5 text-zinc-400 hover:text-zinc-100"><Copy size={14} /></button>
                  </div>
                </div>
              )}
              <div className="text-[11px] text-zinc-500">Steps run on the device on its next check-in. The file link appears below once the copy finishes.</div>
              <button onClick={onClose} className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-300">Close</button>
            </div>
          )}

          {/* recent jobs — surfaces the drive link + per-step outcome as they resolve */}
          {jobs.length > 0 && (
            <div className="pt-2 border-t border-zinc-900">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Recent offboarding</div>
              <div className="space-y-2">
                {jobs.slice(0, 4).map(j => (
                  <div key={j.id} className="text-xs bg-zinc-900/30 border border-zinc-900 rounded p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-200 font-mono">{j.account}</span>
                      <span className={j.status === "completed" ? "text-emerald-400" : j.status === "error" ? "text-red-400" : "text-amber-400"}>{j.status}</span>
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">{fmtTime(j.created_at)}</div>
                    {(j.result?.steps || []).map((s, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[11px] mt-1">
                        {s.status === "ok" ? <Check size={11} className="text-emerald-400" /> : s.status === "skipped" ? <X size={11} className="text-zinc-500" /> : <X size={11} className="text-red-400" />}
                        <span className="text-zinc-400">{s.step}</span>
                        <span className="text-zinc-600 truncate">{s.detail || s.error}</span>
                      </div>
                    ))}
                    {j.drive_url && (
                      <a href={j.drive_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 mt-1.5">
                        <ExternalLink size={11} /> {j.copy_status === "uploaded_drive" ? "Open in Google Drive" : "Download copied files"}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepRow({ on, onToggle, title, hint, danger }) {
  return (
    <button onClick={() => onToggle(!on)} className={`w-full flex items-start gap-3 text-left rounded p-3 border ${on ? (danger ? "bg-red-500/5 border-red-500/30" : "bg-blue-500/5 border-blue-500/30") : "bg-zinc-900/30 border-zinc-900"}`}>
      <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? (danger ? "bg-red-500/80 border-red-400" : "bg-blue-500/80 border-blue-400") : "border-zinc-700"}`}>{on && <Check size={11} className="text-white" />}</span>
      <span className="min-w-0">
        <span className={`block text-sm ${danger && on ? "text-red-200" : "text-zinc-100"}`}>{title}</span>
        <span className="block text-[11px] text-zinc-500 mt-0.5">{hint}</span>
      </span>
    </button>
  );
}

function DiskEncryptionCard({ agent, id, online }) {
  const [fc, setFc] = useState(null);
  const [bl, setBl] = useState(null);
  const [busy, setBusy] = useState("");
  const [revealed, setRevealed] = useState("");

  const loadFc = () => api.get(`/agent/${id}/disk-forecast`).then(r => setFc(r.data)).catch(() => {});
  const loadBl = () => api.get(`/agent/${id}/bitlocker`).then(r => setBl(r.data)).catch(() => {});
  useEffect(() => { loadFc(); loadBl(); }, [id]);

  const enableBl = async () => {
    if (!window.confirm("Enable BitLocker on the system drive? This encrypts the disk (used space first) and escrows the recovery key to the vault. It can take a while and may require a TPM.")) return;
    setBusy("bl");
    try {
      await api.post(`/agent/${id}/bitlocker/enable`);
      toast.success("BitLocker enable queued — key will be escrowed on completion");
      for (let i = 0; i < 20; i++) { await new Promise(r => setTimeout(r, 3000)); const { data } = await api.get(`/agent/${id}/bitlocker`); if (data.ready) { setBl(data); break; } }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const reveal = async () => {
    try { const { data } = await api.get(`/agent/${id}/bitlocker/key`); setRevealed(data.recovery_key); }
    catch (e) { toast.error(e?.response?.data?.detail || "No key"); }
  };

  const total = fc?.total_gb, used = fc?.used_gb, pct = fc?.used_pct;
  return (
    <Card title="Disk & encryption" right={<HardDrive size={15} className="text-zinc-500" />}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="flex justify-between text-sm mb-1"><span className="text-zinc-500 uppercase text-xs">System disk</span>
            <span className="font-mono">{used != null && total ? `${used} / ${total} GB` : pct != null ? `${pct}%` : "—"}</span></div>
          <div className="h-2 bg-zinc-900 rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, pct || 0)}%`, background: (pct || 0) >= 90 ? "#ef4444" : (pct || 0) >= 75 ? "#f59e0b" : "#10b981" }} /></div>
          <div className="text-[11px] text-zinc-600 mt-2">
            {fc?.forecast
              ? <span className={fc.forecast.days_to_full <= 30 ? "text-amber-400" : ""}>Growing {fc.growth_gb_per_day} GB/day · ~{fc.forecast.days_to_90pct}d to 90%, ~{fc.forecast.days_to_full}d to full</span>
              : (fc?.note || "Forecast accumulating…")}
            {fc?.samples != null && <span className="text-zinc-700"> · {fc.samples} sample{fc.samples === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-zinc-500 uppercase text-xs">BitLocker</span>
            <span className={`font-mono text-xs ${bl?.status === "On" ? "text-emerald-400" : "text-zinc-500"}`}>{bl?.status || agent?.bitlocker_status || "unknown"}</span>
          </div>
          {bl?.escrowed
            ? <div className="text-[11px] text-emerald-400/90 flex items-center gap-1.5"><KeyRound size={12} /> recovery key escrowed{bl.escrow?.at ? ` · ${fmtTime(bl.escrow.at)}` : ""}
                <button onClick={reveal} className="ml-1 text-blue-400 hover:text-blue-300 underline">reveal</button></div>
            : <div className="text-[11px] text-zinc-600">No recovery key escrowed yet.</div>}
          {revealed && <div className="mt-1.5 bg-black/40 border border-zinc-800 rounded p-2 font-mono text-[11px] text-amber-300 break-all flex items-center gap-2">{revealed}<button onClick={() => { navigator.clipboard?.writeText(revealed); toast.success("Copied"); }}><Copy size={12} /></button></div>}
          <button onClick={enableBl} disabled={busy === "bl" || bl?.status === "On"}
            className="mt-2 inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-300 disabled:opacity-50">
            <Lock size={12} /> {busy === "bl" ? "Enabling…" : bl?.status === "On" ? "Encrypted" : "Enable BitLocker + escrow"}
          </button>
          {bl?.error && <div className="text-[10px] text-red-400/80 mt-1">{bl.error}</div>}
        </div>
      </div>
    </Card>
  );
}

// generic scan section for the Diagnostics tab (extensions / netdiscovery / stability / signins)
function ScanSection({ id, online, kind, title, icon: Icon, render }) {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const load = () => api.get(`/agent/${id}/scan/${kind}`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  const scan = async () => {
    setScanning(true);
    try {
      await api.post(`/agent/${id}/scan/${kind}`);
      for (let i = 0; i < 22; i++) { await new Promise(r => setTimeout(r, 2500)); const { data } = await api.get(`/agent/${id}/scan/${kind}`); if (data.ready) { setData(data); break; } }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setScanning(false); }
  };
  return (
    <Card title={title} right={<div className="flex items-center gap-2">
      {data?.at && <span className="text-[10px] text-zinc-600">as of {fmtTime(data.at)}</span>}
      <button onClick={scan} disabled={scanning || !online} className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-1.5 text-zinc-300 disabled:opacity-40">
        <RefreshCw size={12} className={scanning ? "animate-spin" : ""} /> {scanning ? "Scanning…" : "Scan"}</button>
    </div>}>
      {!online && <div className="text-[11px] text-amber-500/80 mb-3">Device is offline — scan runs on its next check-in.</div>}
      {!data?.ready ? <div className="text-sm text-zinc-600 flex items-center gap-2"><Icon size={14} /> No data yet — click Scan.</div>
        : data.data == null ? <div className="text-xs text-zinc-500 font-mono whitespace-pre-wrap">{data.raw || "no output"}</div>
        : render(data.data)}
    </Card>
  );
}

function DiagnosticsTab({ id, online }) {
  const arr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  return (
    <div className="space-y-5">
      <ScanSection id={id} online={online} kind="stability" title="Boot & stability" icon={Activity} render={(d) => (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Stat label="Uptime" value={`${d.uptime_hours ?? "—"}h`} />
            <Stat label="Unexpected shutdowns" value={d.unexpected_shutdowns ?? 0} />
            <Stat label="Kernel-power events" value={d.kernel_power_events ?? 0} />
            <Stat label="Bugchecks (BSOD)" value={arr(d.bugchecks).length} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Top crashing apps</div>
          {arr(d.top_app_crashes).length === 0 ? <div className="text-xs text-zinc-600">None recorded.</div> :
            <div className="space-y-1">{arr(d.top_app_crashes).map((c, i) => (
              <div key={i} className="flex justify-between text-xs border-b border-zinc-900/60 pb-1"><span className="text-zinc-300 truncate">{c.app}</span><span className="font-mono text-amber-400">{c.count}</span></div>
            ))}</div>}
        </div>
      )} />

      <ScanSection id={id} online={online} kind="signins" title="Sign-in monitoring (7 days)" icon={LogIn} render={(d) => (
        <div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="Successful" value={d.success_count ?? 0} />
            <Stat label="Failed" value={d.failure_count ?? 0} />
            <Stat label="Lockouts" value={arr(d.lockouts).length} />
          </div>
          {arr(d.top_failed_users).length > 0 && <>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Most failed sign-ins by user</div>
            <div className="space-y-1 mb-3">{arr(d.top_failed_users).map((u, i) => (
              <div key={i} className="flex justify-between text-xs border-b border-zinc-900/60 pb-1"><span className="text-zinc-300">{u.user || "(blank)"}</span><span className="font-mono text-red-400">{u.count}</span></div>
            ))}</div>
          </>}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Recent failures</div>
          {arr(d.recent_failures).length === 0 ? <div className="text-xs text-zinc-600">No failed sign-ins.</div> :
            <div className="space-y-1 max-h-48 overflow-y-auto">{arr(d.recent_failures).map((f, i) => (
              <div key={i} className="flex justify-between text-[11px] font-mono text-zinc-500"><span className="text-zinc-400">{f.user || "(blank)"}</span><span>{fmtTime(f.time)}</span></div>
            ))}</div>}
        </div>
      )} />

      <ScanSection id={id} online={online} kind="netdiscovery" title="Network discovery" icon={Network} render={(d) => (
        d.error ? <div className="text-xs text-amber-500">{d.error}</div> :
        <div>
          <div className="text-[11px] text-zinc-500 mb-2 font-mono">{d.count} host{d.count === 1 ? "" : "s"} on {d.subnet} · this device {d.self}</div>
          <div className="max-h-80 overflow-y-auto"><table className="w-full text-xs"><thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-1">IP</th><th className="text-left">MAC</th><th className="text-left">State</th></tr></thead>
            <tbody>{arr(d.hosts).map((h, i) => (<tr key={i} className="border-t border-zinc-900/60"><td className="py-1 font-mono text-zinc-300">{h.ip}{h.ip === d.self && <span className="text-[9px] text-blue-400 ml-1">this device</span>}</td><td className="font-mono text-zinc-500">{h.mac}</td><td className="text-zinc-500">{h.state}</td></tr>))}</tbody></table></div>
          <div className="text-[10px] text-zinc-600 mt-2">Hosts that responded on the local subnet — includes devices with no agent (printers, switches, cameras, unmanaged PCs).</div>
        </div>
      )} />

      <ScanSection id={id} online={online} kind="extensions" title="Browser extensions" icon={Puzzle} render={(d) => {
        const items = arr(d);
        return items.length === 0 ? <div className="text-xs text-zinc-600">No Chrome/Edge extensions found.</div> :
        <div className="max-h-80 overflow-y-auto"><table className="w-full text-xs"><thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th className="text-left py-1">Extension</th><th className="text-left">Browser</th><th className="text-left">User</th><th className="text-left">Version</th></tr></thead>
          <tbody>{items.map((e, i) => (<tr key={i} className="border-t border-zinc-900/60"><td className="py-1 text-zinc-200">{e.name}<div className="text-[9px] text-zinc-600 font-mono">{e.id}</div></td><td className="text-zinc-400">{e.browser}</td><td className="text-zinc-500">{e.user}</td><td className="font-mono text-zinc-500">{e.version}</td></tr>))}</tbody></table></div>;
      }} />
    </div>
  );
}

function ProcSvcTab({ id, online }) {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [view, setView] = useState("processes");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/agent/${id}/procsvc`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const scan = async () => {
    setScanning(true);
    try {
      await api.post(`/agent/${id}/procsvc/scan`);
      toast.success("Scan requested");
      // poll for the result to land
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const { data } = await api.get(`/agent/${id}/procsvc`);
        if (data.ready) { setData(data); break; }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setScanning(false); }
  };

  const killProc = async (p) => {
    if (!window.confirm(`Kill ${p.name} (PID ${p.pid})?`)) return;
    setBusy("p" + p.pid);
    try { await api.post(`/agent/${id}/process/kill`, { pid: p.pid }); toast.success(`Kill ${p.pid} sent`); setTimeout(scan, 2000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  const svcOp = async (s, op) => {
    if (op !== "start" && !window.confirm(`${op} service "${s.name}"?`)) return;
    setBusy("s" + s.name);
    try { await api.post(`/agent/${id}/service`, { name: s.name, op }); toast.success(`${op} ${s.name} sent`); setTimeout(scan, 2500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };

  const procs = asArray(data?.processes).filter(p => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));
  const svcs = asArray(data?.services).filter(s => !q || `${s.name} ${s.display}`.toLowerCase().includes(q.toLowerCase()));
  const startup = asArray(data?.startup).filter(s => !q || (s.name || "").toLowerCase().includes(q.toLowerCase()));
  const VIEWS = [["processes", `Processes (${asArray(data?.processes).length})`], ["services", `Services (${asArray(data?.services).length})`], ["startup", `Startup (${asArray(data?.startup).length})`]];

  return (
    <Card title="Processes & Services"
      right={<div className="flex items-center gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…" className="bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1 text-xs text-zinc-200 outline-none" />
        <button onClick={scan} disabled={scanning || !online} className="inline-flex items-center gap-1.5 text-xs bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-1.5 text-zinc-300 disabled:opacity-40">
          <RefreshCw size={12} className={scanning ? "animate-spin" : ""} /> {scanning ? "Scanning…" : "Scan"}</button>
      </div>}>
      {!online && <div className="text-[11px] text-amber-500/80 mb-3">Device is offline — scan and control run on its next check-in.</div>}
      {!data?.ready ? (
        <div className="text-sm text-zinc-600">No snapshot yet — click Scan to collect the device's processes, services and startup items.</div>
      ) : (
        <>
          <div className="flex gap-1.5 mb-3">
            {VIEWS.map(([k, label]) => (
              <button key={k} onClick={() => setView(k)} className={`px-2.5 py-1 rounded text-xs border ${view === k ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>{label}</button>
            ))}
            <span className="ml-auto text-[10px] text-zinc-600 self-center">as of {fmtTime(data.at)}</span>
          </div>

          {view === "processes" && (
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono sticky top-0 bg-zinc-950"><tr><th className="text-left py-2">Name</th><th className="text-left">PID</th><th className="text-left">Mem</th><th className="text-left">CPU(s)</th><th></th></tr></thead>
                <tbody>
                  {procs.map(p => (
                    <tr key={p.pid} className="border-t border-zinc-900/60">
                      <td className="py-1.5 text-zinc-200">{p.name}</td>
                      <td className="font-mono text-zinc-500">{p.pid}</td>
                      <td className="font-mono text-zinc-400">{p.mem_mb} MB</td>
                      <td className="font-mono text-zinc-500">{p.cpu}</td>
                      <td className="text-right"><button onClick={() => killProc(p)} disabled={busy === "p" + p.pid} className="text-[11px] text-red-400/80 hover:text-red-300 px-1.5 disabled:opacity-40">Kill</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === "services" && (
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono sticky top-0 bg-zinc-950"><tr><th className="text-left py-2">Service</th><th className="text-left">State</th><th className="text-left">Start</th><th></th></tr></thead>
                <tbody>
                  {svcs.map(s => {
                    const running = (s.state || "").toLowerCase() === "running";
                    return (
                      <tr key={s.name} className="border-t border-zinc-900/60">
                        <td className="py-1.5 text-zinc-200">{s.display || s.name}<div className="text-[10px] text-zinc-600 font-mono">{s.name}</div></td>
                        <td><span className={running ? "text-emerald-400" : "text-zinc-500"}>{s.state}</span></td>
                        <td className="text-zinc-500">{s.start_mode}</td>
                        <td className="text-right whitespace-nowrap">
                          {running
                            ? <><button onClick={() => svcOp(s, "restart")} disabled={busy === "s" + s.name} className="text-[11px] text-blue-400/80 hover:text-blue-300 px-1.5">Restart</button><button onClick={() => svcOp(s, "stop")} disabled={busy === "s" + s.name} className="text-[11px] text-red-400/80 hover:text-red-300 px-1.5">Stop</button></>
                            : <button onClick={() => svcOp(s, "start")} disabled={busy === "s" + s.name} className="text-[11px] text-emerald-400/80 hover:text-emerald-300 px-1.5">Start</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {view === "startup" && (
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono sticky top-0 bg-zinc-950"><tr><th className="text-left py-2">Name</th><th className="text-left">Location</th><th className="text-left">Command</th></tr></thead>
                <tbody>
                  {startup.map((s, i) => (
                    <tr key={i} className="border-t border-zinc-900/60">
                      <td className="py-1.5 text-zinc-200">{s.name}</td>
                      <td className="text-zinc-500">{s.location}</td>
                      <td className="font-mono text-[10px] text-zinc-500 truncate max-w-md">{s.command}</td>
                    </tr>
                  ))}
                  {startup.length === 0 && <tr><td colSpan={3} className="py-4 text-zinc-600">No startup items.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Patches({ id, online }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [kb, setKb] = useState("");
  const load = async () => { const { data } = await api.get(`/agent/${id}/patches`); setData(data); };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);

  const scan = async () => { try { await api.post(`/agent/${id}/patch/scan`); toast.success("Patch scan requested (~30s)"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const install = async () => { const ids = Object.keys(sel).filter((k) => sel[k]); if (!ids.length) return; try { const r = await api.post(`/agent/${id}/patch/install`, { update_ids: ids, reboot_allowed: false }); toast.success(`Install job ${r.data.job_id}`); setSel({}); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const rollback = async () => { if (!kb) return; try { const r = await api.post(`/agent/${id}/patch/uninstall`, { kb }); toast.success(`Rollback job ${r.data.job_id}`); setKb(""); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const updates = data?.updates || [];
  return (
    <Card title={`Available updates (${updates.length})`}
      right={<div className="flex items-center gap-2">
        <input value={kb} onChange={(e) => setKb(e.target.value)} placeholder="KB5030213" className="w-28 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs font-mono" />
        <button onClick={rollback} disabled={!kb || !online} className="bg-zinc-800 disabled:opacity-40 text-zinc-200 text-xs rounded px-3 py-1.5">Rollback</button>
        <button onClick={scan} disabled={!online} className="bg-zinc-800 text-zinc-200 text-xs rounded px-3 py-1.5">Scan</button>
        <button onClick={install} disabled={!Object.values(sel).some(Boolean)} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded px-3 py-1.5">Install selected</button>
      </div>}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono"><tr><th></th><th className="text-left py-2">KB</th><th className="text-left">Severity</th><th className="text-left">Size</th><th className="text-left">Title</th></tr></thead>
        <tbody>
          {updates.map((u) => (
            <tr key={u.update_id} className="border-t border-zinc-900/60">
              <td className="py-2"><input type="checkbox" checked={!!sel[u.update_id]} onChange={(e) => setSel({ ...sel, [u.update_id]: e.target.checked })} /></td>
              <td className="font-mono text-xs">{u.kb || u.update_id.slice(0, 8)}</td>
              <td className="text-xs text-zinc-400">{u.severity || "—"}</td>
              <td className="font-mono text-xs">{u.size_mb} MB</td>
              <td className="text-zinc-300">{u.title}</td>
            </tr>
          ))}
          {updates.length === 0 && <tr><td colSpan={5} className="py-6 text-zinc-500">No updates — run a scan.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function wsUrl(path) {
  const base = (BACKEND || window.location.origin).replace(/^http/, "ws");
  return base + "/api" + path;
}
async function ticket() { const { data } = await api.post("/agent/ws-ticket"); return data.ticket; }

function Terminal({ id, online }) {
  const [lines, setLines] = useState("");
  const [cmd, setCmd] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const outRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    (async () => {
      const t = await ticket();
      ws = new WebSocket(wsUrl(`/agent/${id}/terminal?shell=cmd&token=${encodeURIComponent(t)}`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => setConnected(false);
      ws.onmessage = (e) => setLines((p) => (p + e.data).slice(-60000));
    })();
    return () => ws && ws.close();
  }, [id, online]);
  useEffect(() => { if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight; }, [lines]);

  const send = (e) => { e.preventDefault(); if (wsRef.current?.readyState === 1) { wsRef.current.send(cmd + "\r\n"); setCmd(""); } };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — terminal unavailable.</div>;

  return (
    <div className="bg-black border border-zinc-900 rounded-md overflow-hidden h-[460px] flex flex-col">
      <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2 text-xs"><span className={connected ? "text-emerald-400" : "text-zinc-500"}>● {connected ? "connected" : "connecting…"}</span></div>
      <pre ref={outRef} className="flex-1 overflow-y-auto p-3 font-mono text-[12.5px] whitespace-pre-wrap text-zinc-300">{lines || "…"}</pre>
      <form onSubmit={send} className="flex border-t border-zinc-900"><span className="px-2 py-2 text-emerald-400 font-mono">›</span><input value={cmd} onChange={(e) => setCmd(e.target.value)} autoFocus className="flex-1 bg-black text-zinc-200 font-mono text-sm px-1 outline-none" placeholder="type a command…" /></form>
    </div>
  );
}

function Screen({ id, online }) {
  const [frame, setFrame] = useState(null);
  const [control, setControl] = useState(false);
  const [connected, setConnected] = useState(false);
  // Every failure used to render as "connecting…" forever: an unlicensed module,
  // a role that is not permitted, an agent that cannot capture, nobody signed in
  // at the machine. Four different situations, one blank rectangle. Each now
  // says which one it is.
  const [problem, setProblem] = useState(null);

  const wsRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!online) return;
    let ws;
    setProblem(null);
    (async () => {
      let t;
      try { t = await ticket(); }
      catch { setProblem("Your account is not permitted to open a remote desktop session."); return; }
      ws = new WebSocket(wsUrl(`/agent/${id}/screen?token=${encodeURIComponent(t)}&interval_ms=500&quality=55`));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = (ev) => {
        setConnected(false);
        // 4401 is this server's "refused": the module is off for this
        // deployment, or this role may not view screens.
        if (ev.code === 4401) {
          setProblem("Remote desktop is not enabled for this account. It is a licensed module (SCREEN_VIEW_ENABLED) restricted to specific roles.");
        } else if (!frame) {
          setProblem((p) => p || "The session closed before any frame arrived.");
        }
      };
      ws.onerror = () => setProblem((p) => p || "Could not reach the remote desktop channel.");
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.error) { setProblem(m.error); return; }
        if (m.image_b64) {
          setProblem(null);
          setFrame({ src: `data:image/jpeg;base64,${m.image_b64}`, w: m.width, h: m.height, seq: m.seq });
        }
      };
    })();
    return () => ws && ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, online]);

  const coords = (e) => { const r = imgRef.current.getBoundingClientRect(); return { x: Math.round((e.clientX - r.left) / r.width * frame.w), y: Math.round((e.clientY - r.top) / r.height * frame.h) }; };
  const send = (ev) => { if (control && wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(ev)); };
  if (!online) return <div className="text-zinc-500 text-sm p-6">Agent offline — remote desktop unavailable.</div>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-sm">
        <span className={connected && frame ? "text-emerald-400" : problem ? "text-amber-400" : "text-zinc-500"}>
          ● {connected && frame ? "streaming" : problem ? "unavailable" : "connecting…"}
        </span>
        <label className="flex items-center gap-2 text-zinc-300"><input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} />Take control</label>
        <span className="ml-auto font-mono text-xs text-zinc-600">{frame ? `${frame.w}×${frame.h} #${frame.seq}` : "no frame"}</span>
      </div>
      <div tabIndex={0} onKeyDown={(e) => { if (!control) return; e.preventDefault(); e.key.length === 1 ? send({ kind: "text", text: e.key }) : send({ kind: "key", key: e.key }); }}
        className="bg-black border border-zinc-900 rounded-md overflow-hidden" style={{ outline: control ? "2px solid #2563eb" : "none" }}>
        {!frame && problem ? (
          <div className="p-10 text-center">
            <div className="text-amber-400 text-sm font-medium">Remote desktop unavailable</div>
            <div className="text-zinc-400 text-[13px] mt-2 max-w-lg mx-auto">{problem}</div>
          </div>
        ) : frame ? <img ref={imgRef} src={frame.src} alt="remote" draggable={false}
          onMouseMove={(e) => control && send({ kind: "move", ...coords(e) })}
          onClick={(e) => control && send({ kind: "click", ...coords(e), button: "left" })}
          onContextMenu={(e) => { e.preventDefault(); control && send({ kind: "click", ...coords(e), button: "right" }); }}
          onDoubleClick={(e) => control && send({ kind: "click", ...coords(e), button: "left", double: true })}
          onWheel={(e) => control && send({ kind: "scroll", ...coords(e), dy: e.deltaY > 0 ? -1 : 1 })}
          style={{ width: "100%", cursor: control ? "crosshair" : "default", userSelect: "none" }} />
          : <div className="p-16 text-center text-zinc-500 text-sm">Waiting for first frame…</div>}
      </div>
    </div>
  );
}
